import { useQuery } from "@tanstack/vue-query";
import { type MaybeRef } from "@vueuse/core";
import { api } from "@/api";
import { useCurrentSite } from "@/services/locations/useCurrentSite";
import { STORAGE_QUERY_KEY, type StorageCrossing } from "./storage";

type StorageCrossingQuery = Partial<{
  siteId: string | null;
  bookingId: string;
  status: StorageCrossing["status"] | null;
  search: string;
  fromDt: Date;
  untilDt: Date;
}> &
  PaginationQuery;

export const listStorageCrossings = async (query: StorageCrossingQuery) => {
  const { body } = await api.get<Paginate<StorageCrossing[]>>(
    "/storage/crossings",
    { query },
  );
  return body;
};

/** Compound gate crossings: the site's on the Entry Log, or one booking's. */
export const useStorageCrossings = (query: MaybeRef<StorageCrossingQuery>) => {
  const { id: currentSiteId } = useCurrentSite();

  return useQuery({
    queryKey: [
      STORAGE_QUERY_KEY,
      "crossings",
      computed(() => ({
        // A booking's crossings are its own, whichever site is selected.
        siteId: unref(query).bookingId ? undefined : currentSiteId.value,
        ...unref(query),
      })),
    ] as const,
    queryFn: ({ queryKey: [, , query_] }) =>
      listStorageCrossings(unref(query_)),
    keepPreviousData: true,
    refetchInterval: 30 * 1000,
    refetchIntervalInBackground: false,
  });
};
